<template>
  <template v-for="item in items">
    <ProductSearchResult
      v-if="item.product"
      v-bind="item.product"
      :key="'product_' + item.id"
    />
    <ProductListingTeaser
      v-else
      v-bind="item.paragraph"
      :key="'teaser_' + item.id"
    />
  </template>
</template>

<script lang="ts" setup>
import type { FieldListItem, ItemEditContext } from '#blokkli/types'
import type { ApiSearchProductResultItem } from '~/layers/search/types'
import { paragraphVisibleForRegion } from '~/helpers/paragraphs'
import { INJECT_EDIT_CONTEXT } from '#blokkli/helpers/symbols'
import type { ParagraphPgTeaserFragment } from '#build/graphql-operations'

const { region } = useCountry()

const props = defineProps<{
  teasers: FieldListItem[]
  products: ApiSearchProductResultItem[]
  isEditing: boolean
}>()

// Gives us access to the reactive options in the editor.
const editContext = inject<ItemEditContext | null>(INJECT_EDIT_CONTEXT, null)

type HitItem = {
  id: string
  product?: ApiSearchProductResultItem
  paragraph?: ParagraphPgTeaserFragment
}

function parsePosition(v: string): number {
  const positionNumber = parseInt(v)
  if (positionNumber && Number.isInteger(positionNumber)) {
    return positionNumber
  }

  return 0
}

const items = computed<HitItem[]>(() => {
  const visible: HitItem[] = [
    ...props.products.map((product) => ({ product, id: product.id })),
  ]

  const getPosition = (item: FieldListItem): number => {
    if (props.isEditing) {
      const mutatedOption = editContext?.mutatedOptions[item.uuid]?.position
      if (mutatedOption) {
        return parsePosition(mutatedOption)
      }
    }

    const position = item.options?.position || '0'
    if (position) {
      return parsePosition(position)
    }

    return 0
  }

  const getTeaserParagraph = (item: FieldListItem) => {
    if (item.bundle === 'pg_teaser') {
      return item
    } else if (
      item.bundle === 'from_library' &&
      (item as any)?.props?.libraryItem?.block
    ) {
      return (item as any).props.libraryItem.block
    }
  }

  props.teasers.forEach((item) => {
    const paragraph = getTeaserParagraph(item)
    if (paragraph) {
      // Check the region visibility options of thie paragraph.
      const visibleForRegion = paragraphVisibleForRegion(
        item.options,
        region.value,
      )

      // During editing the teaser should always be added.
      if (visibleForRegion || props.isEditing) {
        const position = getPosition(item)
        visible.splice(position, 0, {
          paragraph: paragraph.props,
          id: item.uuid,
        })
      }
    }
  })

  return visible
})
</script>
